
import { useState, useEffect } from 'react';
import CipherQuizForm from './CipherQuizForm';
import CipherAlreadyCompleted from './CipherAlreadyCompleted';
import { checkWalletInitialized, getExistingBroCode } from '@/lib/broCode';
import { CipherQuestion } from './cipherTypes';
import { LoadingState } from './LoadingState';

interface CipherQuizProps {
  hasToken: boolean;
  walletAddress: `0x${string}`;
  onComplete: (rank: string, brocode: string) => void;
  tableReady?: boolean;
}

const questions: CipherQuestion[] = [
  {
    question: "🔍 What must every Bro complete to join the brotherhood?",
    options: ["The Ledger", "The Ritual", "The Cipher", "The Wager"],
    correctAnswer: "The Cipher",
    hint: "You're taking it right now..."
  },
  {
    question: "💪 In BroMentum, what represents 'discipline, daily'?",
    options: ["Proof", "Rematch", "Clout", "Ritual"],
    correctAnswer: "Ritual",
    hint: "Something you do every day to build discipline"
  },
  {
    question: "👑 What's required to ascend to the highest rank of BROMETHEUS?",
    options: ["5,000 BroScore or 100,000 TOP1", "1,000 BroScore", "10 completed wagers", "Full community vote"],
    correctAnswer: "5,000 BroScore or 100,000 TOP1",
    hint: "It requires either a lot of engagement points or significant token holdings"
  },
  {
    question: "🤖 Which tools are part of the future Brodom ecosystem? (select 3)",
    options: ["Agentic Swarms", "ProofMint NFTs", "A Descrow", "Bro Agents"],
    correctAnswer: "Agentic Swarms, A Descrow, Bro Agents",
    hint: "Think about autonomous systems, escrow services, and AI agents"
  },
  {
    question: "⚠️ What happens if you abandon a wager without completing it?",
    options: ["You're silently removed", "You can retry later", "You auto-forfeit and get penalized", "Your BroCode is rerolled"],
    correctAnswer: "You auto-forfeit and get penalized",
    hint: "Abandoning commitments has consequences in the brotherhood"
  }
];

const CipherQuiz = ({ hasToken, walletAddress, onComplete, tableReady = true }: CipherQuizProps) => {
  const [isAlreadyInitialized, setIsAlreadyInitialized] = useState<boolean>(false);
  const [isCheckingWallet, setIsCheckingWallet] = useState<boolean>(true);
  const [existingBroCode, setExistingBroCode] = useState<string | null>(null);
  const [existingRank, setExistingRank] = useState<string | null>(null);

  // Check if wallet is already initialized
  useEffect(() => {
    const checkIfWalletInitialized = async () => {
      if (!walletAddress || !tableReady) return;

      setIsCheckingWallet(true);
      try {
        // Check if wallet has already taken the cipher
        const isInitialized = await checkWalletInitialized(walletAddress);
        setIsAlreadyInitialized(isInitialized);
        
        if (isInitialized) {
          // Get existing BroCode data
          const broData = await getExistingBroCode(walletAddress);
          if (broData) {
            setExistingBroCode(broData.brocode);
            setExistingRank(broData.rank);
          }
        }
      } catch (error) {
        console.error("Error checking wallet initialization:", error);
      }
      setIsCheckingWallet(false);
    };

    checkIfWalletInitialized();
  }, [walletAddress, tableReady]);

  if (isCheckingWallet) {
    return <LoadingState message="Checking your wallet..." />;
  }

  if (isAlreadyInitialized) {
    return (
      <CipherAlreadyCompleted 
        brocode={existingBroCode || 'Unknown'} 
        rank={existingRank || 'Initiated Bro'}
        onComplete={() => {
          // Redirect to completion with existing data
          if (existingRank && existingBroCode) {
            onComplete(existingRank, existingBroCode);
          }
        }}
      />
    );
  }

  return (
    <CipherQuizForm 
      questions={questions}
      hasToken={hasToken}
      walletAddress={walletAddress}
      onComplete={onComplete}
      tableReady={tableReady}
    />
  );
};

export default CipherQuiz;
